import { useFormikContext } from "formik";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FormikCell, FormikRadioYN } from "@/features/forms/components/formik-controls";
import type { Form8857Data } from "@/features/forms/types";

const TAX_YEAR_KEYS = [
  "tax_year1", "tax_year2", "tax_year3", "tax_year4", "tax_year5", "tax_year6",
] as const;

/**
 * Part I — Should you file this form? (legacy `sr_part1.php`). Two threshold
 * radios and the six tax-year inputs. Answering "No" to Q1 opens an advisory
 * notice popup (it does *not* block further entry or PDF generation — §9).
 */
export function SrPart1({ onNext }: { onNext: () => void }) {
  const { values } = useFormikContext<Form8857Data>();
  const [noticeOpen, setNoticeOpen] = useState(false);
  const prev = useRef(values.para_desc);

  useEffect(() => {
    if (values.para_desc === "N" && prev.current !== "N") setNoticeOpen(true);
    prev.current = values.para_desc;
  }, [values.para_desc]);

  return (
    <div className="flex flex-col gap-6">
      <Card className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-2">
          <span className="text-foreground text-sm">
            <span className="text-muted-foreground mr-2 font-mono">1</span>
            Do either of the two threshold conditions (jointly-liable erroneous item,
            or community-property relief) describe your situation?
          </span>
          <FormikRadioYN name="para_desc" aria-label="Threshold conditions apply" />
        </div>
        <div className="flex flex-col gap-2 border-t pt-4">
          <span className="text-foreground text-sm">
            <span className="text-muted-foreground mr-2 font-mono">2</span>
            Did the IRS take your share of a joint refund to pay a past-due debt owed
            only by the person on line 6 (child/spousal support, student loan, taxes)?
          </span>
          <FormikRadioYN name="irs_take_share" aria-label="IRS took your share of a joint refund" />
        </div>
      </Card>

      <Card className="flex flex-col gap-3 p-6">
        <Label>
          <span className="text-muted-foreground mr-2 font-mono">3</span>
          Tax year(s) you want innocent spouse relief
        </Label>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {TAX_YEAR_KEYS.map((key, i) => (
            <FormikCell
              key={key}
              name={key}
              aria-label={`Tax year ${i + 1}`}
              placeholder="e.g. 2020"
              maxLength={20}
            />
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={onNext}>
          Go to Part II <ArrowRight />
        </Button>
      </div>

      <Dialog open={noticeOpen} onOpenChange={setNoticeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Check your eligibility</DialogTitle>
            <DialogDescription>
              If neither of the two conditions apply, do NOT file this Form 8857. You
              may still go to line 2 to see whether a different form fits. This is
              advisory only — you can continue and still generate the form.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => setNoticeOpen(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
